if (!customElements.get('product-reviews-v2')) {
  customElements.define('product-reviews-v2', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.legacy = this.parentElement.querySelector('.product-reviews');
      this.widget = this.legacy?.querySelector('ryviu-widget');
      if (!this.widget) return;

      this.controller = new AbortController();
      this.loader = this.querySelector('.product-reviews-v2__loading');
      this.preview = this.querySelector('.product-reviews-v2__preview');
      this.dialog = this.querySelector('.product-reviews-v2__dialog');
      this.content = this.querySelector('.product-reviews-v2__dialog-content');
      this.openButton = this.querySelector('[data-reviews-open]');
      this.writeButton = this.querySelector('[data-reviews-write]');
      this.photoSliders = this.querySelectorAll('.product-reviews-v2__photos');
      this.placeholder = document.createComment('Ryviu original position');
      const options = { signal: this.controller.signal };

      this.addEventListener('click', event => this.handleClick(event), options);
      this.dialog.addEventListener('close', () => {
        this.dialog.classList.remove('is-closing');
        this.openButton.setAttribute('aria-expanded', 'false');
      }, options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.closeDrawer();
      }, options);
      this.dialog.addEventListener('animationend', event => {
        if (event.target === this.dialog && event.animationName === 'product-reviews-v2-drawer-out') {
          this.closeDrawer(true);
        }
      }, options);
      this.addEventListener('load', () => {
        this.scheduleSync();
        this.refreshLayout();
      }, { ...options, capture: true });
      this.widget.addEventListener('vce-ready', () => this.scheduleSync(), options);
      this.widgetObserver = new MutationObserver(() => this.scheduleSync());
      this.widgetObserver.observe(this.widget, {
        childList: true, subtree: true, attributes: true, characterData: true,
        attributeFilter: ['class', 'style', 'src', 'data-width', 'datetime']
      });
      this.variantObserver = new MutationObserver(() => this.syncVariant());
      this.variantObserver.observe(document.documentElement, {
        attributes: true, attributeFilter: ['data-product-reviews-variant']
      });
      this.variantObserver.observe(this, { attributes: true, attributeFilter: ['data-variant'] });
      window.addEventListener('load', () => this.scheduleSync(), options);

      const section = this.closest('.shopify-section') || this.parentElement;
      section.addEventListener('click', event => {
        if (!this.active || !event.target.closest('.product-info > .product__stars')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openDrawer();
      }, { ...options, capture: true });
      this.syncVariant();
    }

    disconnectedCallback() {
      this.deactivate();
      this.controller?.abort();
      this.widgetObserver?.disconnect();
      this.variantObserver?.disconnect();
      cancelAnimationFrame(this.syncFrame);
      cancelAnimationFrame(this.layoutFrame);
      this.controller = null;
    }

    syncVariant() {
      const requested = new URLSearchParams(window.location.search).has('reviews-v2')
        || this.dataset.variant === 'v2' || document.documentElement.dataset.productReviewsVariant === 'v2';
      const review = this.widget.querySelector('.r--default-item');
      const empty = !this.active && !review && this.widget.querySelector('.Ryviu-Empty-Box');
      if (!requested || empty) {
        this.deactivate();
        return;
      }
      this.observePhotos();
      if (!this.legacy.classList.contains('product-reviews--v2-loading')) {
        this.legacy.classList.add('product-reviews--v2-loading');
        this.refreshLayout();
      }
      if (!this.active && review) {
        this.active = true;
        this.legacyWasHidden = this.legacy.hidden;
        this.minHeight = [this.widget.style.getPropertyValue('min-height'), this.widget.style.getPropertyPriority('min-height')];
        this.widget.before(this.placeholder);
        this.querySelector('.product-reviews-v2__widget').append(this.widget);
        this.legacy.hidden = true;
        this.hidden = false;
      }
      this.updateLoading();
      if (this.active) this.syncContent();
    }

    updateLoading() {
      const loading = !this.active && !this.loadingTimedOut && this.ryviuInstance?.mainLoading !== 'error';
      const wasLoading = !this.loader.hidden;
      if (loading && !this.loadingTimer) {
        this.loadingTimer = setTimeout(() => {
          this.loadingTimedOut = true;
          this.updateLoading();
        }, 30000);
      }
      if (!loading) {
        clearTimeout(this.loadingTimer);
        this.loadingTimer = null;
      }
      this.loader.hidden = !loading;
      this.preview.hidden = !this.active;
      this.hidden = !loading && !this.active;
      if (wasLoading !== loading) this.refreshLayout();
    }

    deactivate() {
      if (!this.controller) return;
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
      this.loadingTimedOut = false;
      this.loader.hidden = true;
      this.preview.hidden = false;
      this.hidden = true;
      this.stopInfiniteLoading();
      this.unwatchPhotos?.();
      this.unwatchPhotos = null;
      this.ryviuInstance = null;
      if (this.legacy?.classList.contains('product-reviews--v2-loading')) {
        this.legacy.classList.remove('product-reviews--v2-loading');
        this.refreshLayout();
      }
      if (!this.active) return;
      this.active = false;
      this.closeDrawer(true);
      if (this.galleryOpen) window.Spotlight?.close();
      this.reviewSwiper?.destroy(true, true);
      this.reviewSwiper = null;
      for (const slider of this.photoSliders) slider.swiper?.destroy(true, true);
      this.signature = null;
      this.placeholder.replaceWith(this.widget);
      if (this.minHeight[0]) {
        this.widget.style.setProperty('min-height', ...this.minHeight);
      } else {
        this.widget.style.removeProperty('min-height');
      }
      this.legacy.hidden = this.legacyWasHidden;
      this.hidden = true;
      this.refreshLayout();
    }

    scheduleSync() {
      if (this.syncFrame) return;
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.syncVariant();
      });
    }

    observePhotos() {
      if (this.unwatchPhotos) return;
      const root = this.widget.getVueInstance?.();
      const instance = root?.ryviu_widget ? root : root?.$children?.find(child => child.ryviu_widget);
      if (!instance?.ryviu_widget || !instance.$watch) return;
      this.ryviuInstance = instance;
      this.unwatchPhotos = instance.$watch(
        () => instance.ryviu_widget?.reviews_images?.images_list,
        () => this.scheduleSync(),
        { deep: true }
      );
    }

    getPhotoUrls() {
      const images = this.ryviuInstance?.ryviu_widget?.reviews_images?.images_list;
      if (Array.isArray(images)) {
        return images.map(image => image?.url).filter(url => typeof url === 'string' && url);
      }
      const thumbnails = this.widget.querySelectorAll('.Ryviu-Thumbnail img, .Ryviu-Head-Images img');
      const urls = Array.from(thumbnails, image => image.currentSrc || (image.getAttribute('src') && image.src));
      return [...new Set(urls.filter(Boolean))];
    }

    syncContent() {
      if (this.widget.style.getPropertyValue('min-height') !== '0px') {
        this.minHeight = [this.widget.style.getPropertyValue('min-height'), this.widget.style.getPropertyPriority('min-height')];
        this.widget.style.setProperty('min-height', '0px', 'important');
      }
      this.writeButton.disabled = !this.widget.querySelector('#show-modal, .Ryviu-Write-Button');
      const rows = [...this.widget.querySelectorAll('.r--rateList tr')];
      for (const distribution of this.querySelectorAll('.product-reviews-v2__distribution')) {
        distribution.hidden = rows.length !== 5;
        rows.forEach((row, index) => {
          const rating = distribution.querySelector(`[data-rating="${5 - index}"]`);
          const bar = row.querySelector('[data-width]');
          const percent = Math.min(100, Math.max(0, Number(bar?.dataset.width) || 0));
          rating.style.setProperty('--rating-width', `${percent}%`);
          rating.setAttribute('aria-label', `${5 - index}/5 (${row.querySelector('.count-percent')?.textContent.trim() || 0})`);
        });
      }
      this.syncInfiniteLoading();
      const items = [...this.widget.querySelectorAll('.r--default-item')].slice(0, 6);
      const photos = this.getPhotoUrls();
      const signature = items.map(item => [
        item.querySelector('.r--content-review')?.textContent,
        item.querySelector('.r--title-review')?.textContent,
        item.querySelector('.Ryviu-Author-Name')?.textContent,
        item.querySelector('.r--date-ryviu, time')?.textContent,
        item.querySelector('.r--stars-item')?.outerHTML,
        item.querySelector('.r-icon--check')?.outerHTML
      ].join('')).join('') + photos.join('\n');
      if (signature !== this.signature) {
        this.signature = signature;
        this.renderCards(items);
        this.renderPhotos(photos);
        this.refreshLayout();
      }
      this.initSlider();
      this.initMediaSlider();
    }

    renderCards(items) {
      this.reviewSwiper?.destroy(true, true);
      this.reviewSwiper = null;
      const wrapper = this.querySelector('.product-reviews-v2__slider .swiper-wrapper');
      wrapper.replaceChildren();
      const template = this.querySelector('[data-review-template]');
      for (const [index, item] of items.entries()) {
        const card = template.content.firstElementChild.cloneNode(true);
        const stars = item.querySelector('[refsetting="star"] .r--stars');
        if (stars) {
          const copy = stars.cloneNode(true);
          copy.querySelector('.r--stars-item')?.style.removeProperty('width');
          copy.setAttribute('aria-hidden', 'true');
          const container = card.querySelector('.product-reviews-v2__card-stars');
          container.setAttribute('role', 'img');
          container.setAttribute('aria-label', `${stars.querySelectorAll('.r--stars-icon:not(.r-stbk)').length}/5`);
          container.append(copy);
        }
        const review = this.ryviuInstance?.ryviu_widget?.reviews?.[index];
        const heading = card.querySelector('.product-reviews-v2__card-title');
        const title = item.querySelector('.r--title-review')?.textContent.trim()
          || this.getReviewText(review?.title);
        heading.textContent = title;
        heading.hidden = !title;
        const content = item.querySelector('.r--content-review')?.cloneNode(true);
        const originalMore = content?.querySelector('.r--show-more');
        originalMore?.remove();
        const excerpt = content?.textContent.trim() || '';
        const paragraph = card.querySelector('.product-reviews-v2__card-text');
        paragraph.hidden = !excerpt;
        const text = paragraph.querySelector('[data-review-excerpt]');
        const more = paragraph.querySelector('.product-reviews-v2__card-more');
        const fullText = this.getReviewText(review?.body_text);
        text.textContent = excerpt;
        if (originalMore && fullText.length > excerpt.length && fullText.startsWith(excerpt)) {
          more.hidden = false;
          more.addEventListener('click', event => {
            if (event.defaultPrevented) return;
            text.textContent = fullText;
            more.setAttribute('aria-expanded', 'true');
            more.hidden = true;
            paragraph.tabIndex = -1;
            paragraph.focus({ preventScroll: true });
            this.reviewSwiper?.update();
            this.refreshLayout();
          });
        }
        const meta = card.querySelector('.product-reviews-v2__card-meta');
        const date = item.querySelector('.r--date-ryviu, time')?.textContent.trim();
        meta.textContent = [date, item.querySelector('.Ryviu-Author-Name')?.textContent.trim()].filter(Boolean).join(' • ');
        const verified = item.querySelector('.r-icon--check');
        if (verified) {
          const badge = verified.cloneNode(true);
          badge.setAttribute('role', 'img');
          badge.setAttribute('aria-label', verified.closest('.r--verified-review')?.querySelector('.text')?.textContent.trim() || '');
          meta.append(badge);
        }
        for (const element of card.querySelectorAll('[id], [local_widget]')) {
          element.removeAttribute('id');
          element.removeAttribute('local_widget');
        }
        wrapper.append(card);
      }
      this.querySelector('.product-reviews-v2__carousel').hidden = items.length === 0;
    }

    getReviewText(value) {
      if (typeof value !== 'string') return '';
      const text = this.ryviuInstance.escapeHtml?.(value) ?? value;
      return text.trim();
    }

    renderPhotos(photos) {
      this.photos = photos;
      this.querySelector('[data-drawer-media]').hidden = !photos.length;
      const track = document.createElement('div');
      track.className = 'swiper-wrapper';
      photos.forEach((source, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-reviews-v2__photo swiper-slide';
        button.dataset.photoIndex = index;
        button.setAttribute('aria-label', this.dataset.photoLabel.replace('[number]', index + 1));
        const img = document.createElement('img');
        img.src = source.replace(
          /^(https?:\/\/imgs\.ryviu\.com\/[^?#]+)\/([^/?#]+)([?#].*)?$/,
          (_, path, filename, suffix = '') => `${path.replace(/\/\d+x\d+\/[^/]+$/, '')}/300x300/jpg/${filename}${suffix}`
        );
        img.alt = '';
        img.width = 100;
        img.height = 100;
        img.loading = 'lazy';
        img.draggable = false;
        button.append(img);
        track.append(button);
      });
      for (const slider of this.photoSliders) {
        slider.swiper?.destroy(true, true);
        slider.hidden = !photos.length;
        slider.replaceChildren(track.cloneNode(true));
      }
    }

    initSlider() {
      const slider = this.querySelector('.product-reviews-v2__slider');
      if (this.reviewSwiper || !window.Swiper || !slider.clientWidth || !slider.querySelector('.swiper-slide')) return;
      const updateFocus = swiper => {
        for (const slide of swiper.slides) slide.inert = !slide.classList.contains('swiper-slide-visible');
      };
      this.reviewSwiper = new window.Swiper(slider, {
        slidesPerView: 'auto',
        spaceBetween: parseFloat(getComputedStyle(slider).columnGap),
        watchSlidesProgress: true,
        watchOverflow: true,
        speed: matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
        on: {
          init: updateFocus,
          slideChange: updateFocus,
          transitionEnd: updateFocus,
          resize: updateFocus,
          beforeResize: swiper => {
            swiper.params.spaceBetween = parseFloat(getComputedStyle(swiper.el).columnGap);
          }
        }
      });
    }

    openDrawer(animate = true) {
      this.dialog.classList.remove('is-closing');
      this.dialog.classList.toggle('is-restoring', !animate);
      if (!this.dialog.open) this.dialog.showModal();
      this.initMediaSlider();
      this.querySelector('[data-drawer-photos]').swiper?.update();
      this.openButton.setAttribute('aria-expanded', 'true');
      this.syncInfiniteLoading();
    }

    closeDrawer(immediate = false) {
      if (!this.dialog.open) return;
      this.dialog.classList.remove('is-restoring');
      if (immediate || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.dialog.classList.remove('is-closing');
        this.dialog.close();
        this.openButton.setAttribute('aria-expanded', 'false');
      } else {
        this.dialog.classList.add('is-closing');
      }
    }

    initMediaSlider() {
      if (!window.Swiper || !this.photos?.length) return;
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      for (const slider of this.photoSliders) {
        if (slider.swiper || !slider.clientWidth) continue;
        new window.Swiper(slider, {
          slidesPerView: 'auto',
          slidesPerGroupAuto: true,
          spaceBetween: parseFloat(getComputedStyle(slider).columnGap),
          watchOverflow: true,
          speed: reducedMotion ? 0 : 300,
          freeMode: { enabled: true, momentum: !reducedMotion },
          mousewheel: { enabled: true, forceToAxis: true, releaseOnEdges: true },
          simulateTouch: true,
          grabCursor: true,
          a11y: {
            slideRole: 'button',
            slideLabelMessage: this.dataset.photoLabel.replace('[number]', '{{index}}')
          },
          on: {
            beforeResize: swiper => {
              swiper.params.spaceBetween = parseFloat(getComputedStyle(swiper.el).columnGap);
            }
          }
        });
      }
    }

    syncInfiniteLoading() {
      const target = this.widget.querySelector('.Ry-Load-More');
      if (target !== this.loadTarget) {
        this.stopInfiniteLoading();
        if (!target) return;
        this.loadTarget = target;
        this.loadTargetWasInert = target.inert;
        target.inert = true;
        this.loadObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.loadNextReviews();
          else this.loadRequestedCount = null;
        }, { root: this.content });
        this.loadObserver.observe(target);
      }
      const button = target?.querySelector('.Ry-Text-Load-More');
      if (button) button.dataset.reviewsLoadingLabel = this.dataset.loadingLabel;
      this.loadNextReviews();
    }

    loadNextReviews() {
      if (!this.active || !this.dialog.open || this.dialog.classList.contains('is-closing') || !this.loadTarget) return;
      const button = this.loadTarget.querySelector('.Ry-Text-Load-More');
      if (!button || button.disabled || button.classList.contains('Polaris-Button--loading')) return;
      const root = this.content.getBoundingClientRect();
      const target = this.loadTarget.getBoundingClientRect();
      if (target.bottom <= root.top || target.top >= root.bottom) return;
      const count = this.widget.querySelectorAll('.r--default-item').length;
      if (this.loadRequestedCount === count) return;
      this.loadRequestedCount = count;
      button.click();
    }

    stopInfiniteLoading() {
      this.loadObserver?.disconnect();
      this.loadObserver = null;
      if (this.loadTarget) {
        this.loadTarget.inert = this.loadTargetWasInert;
        this.loadTarget.querySelector('.Ry-Text-Load-More')?.removeAttribute('data-reviews-loading-label');
      }
      this.loadTarget = null;
      this.loadRequestedCount = null;
    }

    openPhotoGallery(index, trigger) {
      if (!this.photos?.[index] || !window.Spotlight || !window.showSpotlight) return;
      const wasDrawerOpen = this.dialog.open;
      const scrollTop = this.content.scrollTop;
      this.closeDrawer(true);
      this.galleryOpen = true;
      window.showSpotlight(this.photos.map(src => ({ src })), index, {
        onclose: () => {
          this.galleryOpen = false;
          requestAnimationFrame(() => {
            if (!this.active || !this.isConnected || this.galleryOpen) return;
            if (wasDrawerOpen) {
              this.openDrawer(false);
              this.content.scrollTop = scrollTop;
            }
            trigger.focus({ preventScroll: true });
          });
        }
      });
    }

    handleClick(event) {
      if (event.target === this.dialog || event.target.closest('[data-reviews-close]')) return this.closeDrawer();
      if (event.target.closest('[data-reviews-open]')) return this.openDrawer();
      const photo = event.target.closest('[data-photo-index]');
      if (photo && !event.defaultPrevented) return this.openPhotoGallery(Number(photo.dataset.photoIndex), photo);
      if (event.target.closest('[data-reviews-write]')) {
        const button = this.widget.querySelector('#show-modal, .Ryviu-Write-Button');
        this.closeDrawer(true);
        button?.click();
        return;
      }
      if (event.target.closest('.product-reviews-v2__widget .Ryviu-Write-Button, .product-reviews-v2__widget .Ryviu-Thumbnail')) {
        this.closeDrawer(true);
      }
    }

    refreshLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => window.ScrollTrigger?.refresh());
    }
  });
}
